import React, { useState, useRef, useEffect, useCallback } from "react";
import { Sparkles, Send, Loader2 } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import {
  getPrimaryText,
  getSecondaryText,
  getMutedText,
  getInputStyles,
} from "../../utils/themeUtils";
import { sendStaffAiMessage } from "../../api/aiAPI";
import StaffAnswerText from "./StaffAnswerText";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
}

interface StaffAIAssistantPanelProps {
  /**
   * Optional handler so the parent (ChatWidget) can offer a "New chat"
   * affordance in its own header. When omitted, the panel renders an
   * inline "New chat" button next to the empty-state hint.
   */
  onNewChat?: () => void;
  /**
   * Bound externally to the parent's "New chat" button. When the parent
   * increments this counter the panel resets its conversation. We accept
   * a number rather than wiring through an imperative ref so the parent
   * stays in control.
   */
  resetCounter?: number;
  /**
   * A prompt to prefill into the composer (NOT auto-sent) — e.g. handed in
   * from the global search bar's "Not finding…" row. Applied whenever
   * `promptNonce` changes AND `initialPrompt` is set, starting a fresh
   * conversation. `onPromptConsumed` is called once it's been applied so the
   * parent can clear it — otherwise a later remount (e.g. opening the
   * assistant manually) would re-inject the stale prompt and wipe the chat.
   */
  initialPrompt?: string;
  promptNonce?: number;
  onPromptConsumed?: () => void;
}

const SESSION_STORAGE_KEY = "staff_ai_session_id";

/**
 * Embeddable staff AI assistant view. Renders inside the ChatWidget panel
 * (so we have a single floating button, not two). Uses the orchestrator
 * via /api/ai/staff-chat — has tool access (lookup_member, lookup_athlete,
 * search_knowledge_base) and skips identity verification (staff is
 * already authenticated).
 *
 * Conversation persists for the session via session_id in sessionStorage
 * so refresh doesn't lose context. Parent can request a reset by bumping
 * `resetCounter`.
 */
const StaffAIAssistantPanel: React.FC<StaffAIAssistantPanelProps> = ({ resetCounter, initialPrompt, promptNonce, onPromptConsumed }) => {
  const { isDarkMode } = useTheme();
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(() =>
    sessionStorage.getItem(SESSION_STORAGE_KEY)
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Reset on parent request.
  useEffect(() => {
    if (resetCounter === undefined) return;
    setMessages([]);
    setSessionId(null);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  }, [resetCounter]);

  // Prefill from an external prompt (e.g. the search bar's "Not finding…"
  // row): start a fresh conversation and drop the text into the composer so
  // the user can review/edit before sending. Keyed on the nonce so repeated
  // asks with identical text still apply. The `initialPrompt` guard is what
  // makes this consume-once: the parent clears it via onPromptConsumed, so a
  // later mount (opening the assistant manually) sees no prompt and leaves
  // the existing conversation alone. `initialPrompt` is read at fire time
  // (it's set together with the nonce by the parent).
  useEffect(() => {
    if (!promptNonce || !initialPrompt) return;
    setMessages([]);
    setSessionId(null);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    setInput(initialPrompt);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        const len = el.value.length;
        el.setSelectionRange(len, len);
      }
    });
    onPromptConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptNonce]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || sending) return;

    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      role: "user",
      content: text,
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setSending(true);

    const res = await sendStaffAiMessage(text, sessionId || undefined);
    setSending(false);

    if (res.success && res.data) {
      if (res.data.session_id && res.data.session_id !== sessionId) {
        setSessionId(res.data.session_id);
        sessionStorage.setItem(SESSION_STORAGE_KEY, res.data.session_id);
      }
      const aiMsg: ChatMessage = {
        id: `a-${Date.now()}`,
        role: "assistant",
        content: res.data.response || "(empty response)",
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, aiMsg]);
    } else {
      const errMsg: ChatMessage = {
        id: `e-${Date.now()}`,
        role: "assistant",
        content: `⚠️ ${res.error || "Failed to send"}`,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, errMsg]);
    }
  }, [input, sending, sessionId]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {messages.length === 0 && (
          <div className={`p-3 rounded-lg ${isDarkMode ? "bg-purple-950/30" : "bg-purple-50"} text-sm`}>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={14} className="text-purple-500" />
              <p className={`font-semibold ${getPrimaryText(isDarkMode)}`}>
                Hi! I can help with:
              </p>
            </div>
            <ul className={`text-xs space-y-1 list-disc list-inside ${getSecondaryText(isDarkMode)}`}>
              <li>Look up a member or athlete by name/email</li>
              <li>Find policies in your knowledge base</li>
              <li>Answer "where do I find X" questions</li>
              <li>Summarize an account's history</li>
            </ul>
            <p className={`text-xs mt-2 ${getMutedText(isDarkMode)}`}>
              Try: <em>"What's the Johnson family's balance?"</em>
            </p>
          </div>
        )}
        {messages.map((m) => (
          <div
            key={m.id}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[85%] px-3 py-2 rounded-lg text-sm whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-purple-600 text-white"
                  : isDarkMode
                  ? "bg-gray-800 text-gray-100"
                  : "bg-gray-100 text-gray-900"
              }`}
            >
              {m.role === "assistant" ? <StaffAnswerText content={m.content} /> : m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${isDarkMode ? "bg-gray-800 text-gray-300" : "bg-gray-100 text-gray-600"}`}>
              <Loader2 size={14} className="animate-spin" /> Thinking…
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className={`px-3 py-2 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
        <div className="flex items-end gap-2">
          <textarea
            aria-label="Ask anything"
            ref={inputRef}
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything…"
            disabled={sending}
            className={`flex-1 p-2 rounded border text-sm resize-none ${getInputStyles(isDarkMode)}`}
            style={{ minHeight: "38px", maxHeight: "120px" }}
          />
          <button aria-label="Send message"
            type="button"
            onClick={handleSend}
            disabled={sending || !input.trim()}
            className="p-2 rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
          >
            <Send size={16} />
          </button>
        </div>
        <p className={`text-[10px] mt-1 ${getMutedText(isDarkMode)}`}>
          Enter to send · Shift+Enter for new line
        </p>
      </div>
    </div>
  );
};

export default StaffAIAssistantPanel;
